import { buildApiUrl } from '../../utils/apiUrl';
import { appelAuthentifie, requete } from './assistants';

export interface SourceAutorite {
  type_source: string;
  rang: number;
  libelle: string;
  description: string;
}

export interface ApercuMemoire {
  documents: number;
  documents_total: number;
  fiches: number;
  faits: number;
  faits_remplaces: number;
  entites: number;
  conflits_ouverts: number;
  fraicheur: { chaud: number; tiede: number; froid: number };
  occupation: { caracteres: number; budget: number };
  sources: SourceAutorite[];
}

export interface DocumentMemoire {
  id: number;
  nom: string;
  type_source: string;
  type_source_libelle: string;
  date_document: string | null;
  actif: boolean;
  caracteres: number;
  faits: number | null;
  faits_extraits_le: string | null;
  depose_par: string;
  depose_le: string;
  fichier: boolean;
}

export interface FicheMemoire {
  id: number;
  titre: string;
  contenu: string;
  version: number;
  maj_par: string;
  maj_le: string;
  versions?: { version: number; titre: string; maj_par: string; maj_le: string; contenu: string }[];
}

export type Fraicheur = 'chaud' | 'tiede' | 'froid';

export interface FaitMemoire {
  id: number;
  entite: string;
  entite_id: number;
  genre: string;
  attribut: string;
  valeur: string;
  periode: string;
  type_source: string;
  type_source_libelle: string;
  source: string;
  document: number | null;
  extrait: string;
  affirme_le: string;
  fraicheur: Fraicheur;
  statut: 'actif' | 'remplace';
  remplace_par: number | null;
}

export interface ConflitMemoire {
  id: number;
  entite: string;
  attribut: string;
  periode: string;
  statut: 'ouvert' | 'tranche';
  gagnant: number | null;
  tranche_par: string;
  tranche_le: string | null;
  cree_le: string;
  faits: FaitMemoire[];
}

/** Une compétence : un skill importé, devenu assistant de la plateforme. */
export interface Competence {
  id: number;
  title: string;
  summary?: string;
  description: string;
  category_name?: string;
  category_code?: string;
  output_format: string;
  system_prompt?: string;
  cadrage_actif: boolean;
  chiffres_nationaux: boolean;
  is_active: boolean;
  mode: 'lien' | 'natif';
  order?: number;
  updated_at?: string;
  created_by_name?: string;
}

export interface ReferenceCompetence {
  id: number;
  name: string;
  caracteres: number;
  uploaded_at: string;
}

export interface ReglagesModeles {
  defaut: string;
  catalogue: { id: string; nom: string; prix_entree: number; prix_sortie: number; note: string }[];
  usages: { cle: string; libelle: string; description: string; modele: string; regle: boolean }[];
  assistants: { id: number; titre: string; modele: string }[];
  autres: { usage: string; moteur: string; note: string }[];
}

const json = (corps: unknown, methode = 'POST'): RequestInit => ({
  method: methode,
  body: JSON.stringify(corps),
});

export const memoireService = {
  apercu: () => requete<ApercuMemoire>('/documents/memoire/apercu/'),

  sources: () => requete<SourceAutorite[]>('/documents/memoire/sources/'),
  reordonner: (ordre: string[]) =>
    requete<{ faits_rearbitres: number; sources: SourceAutorite[] }>(
      '/documents/memoire/sources/', json({ ordre }), 120000,
    ),

  documents: () => requete<DocumentMemoire[]>('/documents/memoire/documents/'),
  async verserDocument(fichier: File, champs: { nom?: string; type_source: string; date_document?: string }) {
    const corps = new FormData();
    corps.append('fichier', fichier, fichier.name);
    if (champs.nom) corps.append('nom', champs.nom);
    corps.append('type_source', champs.type_source);
    if (champs.date_document) corps.append('date_document', champs.date_document);
    const reponse = await appelAuthentifie(buildApiUrl('/documents/memoire/documents/'), {
      method: 'POST',
      body: corps,
    });
    const donnees = await reponse.json().catch(() => ({}));
    if (!reponse.ok) throw new Error(donnees.detail || 'Le document n\'a pas pu être versé.');
    return donnees as DocumentMemoire;
  },
  modifierDocument: (id: number, champs: Partial<Pick<DocumentMemoire, 'nom' | 'actif' | 'type_source' | 'date_document'>>) =>
    requete<DocumentMemoire>(`/documents/memoire/documents/${id}/`, json(champs, 'PATCH')),
  async supprimerDocument(id: number) {
    const reponse = await appelAuthentifie(buildApiUrl(`/documents/memoire/documents/${id}/`), {
      method: 'DELETE',
    });
    if (!reponse.ok && reponse.status !== 204) throw new Error('Suppression impossible.');
  },
  extraireFaits: (id: number) =>
    requete<{ tache: number }>(`/documents/memoire/documents/${id}/extraire/`, { method: 'POST' }),
  texteDocument: (id: number) =>
    requete<{ nom: string; contenu: string }>(`/documents/memoire/documents/${id}/texte/`),
  suivreTache: (id: number) =>
    requete<{ etat: string; avancement?: string; erreur?: string; resultat?: any }>(
      `/documents/conversations/taches/${id}/`,
    ),

  fiches: () => requete<FicheMemoire[]>('/documents/memoire/fiches/'),
  fiche: (id: number) => requete<FicheMemoire>(`/documents/memoire/fiches/${id}/`),
  creerFiche: (titre: string, contenu: string) =>
    requete<FicheMemoire>('/documents/memoire/fiches/', json({ titre, contenu })),
  modifierFiche: (id: number, titre: string, contenu: string) =>
    requete<FicheMemoire>(`/documents/memoire/fiches/${id}/`, json({ titre, contenu }, 'PATCH')),
  async supprimerFiche(id: number) {
    const reponse = await appelAuthentifie(buildApiUrl(`/documents/memoire/fiches/${id}/`), {
      method: 'DELETE',
    });
    if (!reponse.ok && reponse.status !== 204) throw new Error('Suppression impossible.');
  },

  faits: (params: { q?: string; tous?: boolean } = {}) => {
    const recherche = new URLSearchParams();
    if (params.q) recherche.set('q', params.q);
    if (params.tous) recherche.set('tous', '1');
    const suffixe = recherche.toString() ? `?${recherche}` : '';
    return requete<FaitMemoire[]>(`/documents/memoire/faits/${suffixe}`);
  },
  historiqueFait: (id: number) => requete<FaitMemoire[]>(`/documents/memoire/faits/${id}/historique/`),
  corriger: (champs: {
    entite: string; genre: string; attribut: string; valeur: string; periode?: string; justification?: string;
  }) => requete<FaitMemoire>('/documents/memoire/faits/', json(champs)),

  async competences() {
    // La liste est paginée : sans suivre les pages, le catalogue s'arrêtait
    // au vingtième assistant et les suivants disparaissaient sans erreur.
    let adresse: string | null = '/documents/usecase-templates/?page_size=100';
    const toutes: Competence[] = [];
    while (adresse) {
      const page: any = await requete<any>(adresse);
      toutes.push(...(page.results || page));
      adresse = page.next ? page.next.replace(/^.*\/api\/v1/, '') : null;
    }
    return toutes.filter((c) => c.mode === 'natif');
  },
  competence: (id: number) => requete<Competence>(`/documents/usecase-templates/${id}/`),
  modifierCompetence: (id: number, champs: Partial<Competence>) =>
    requete<Competence>(`/documents/usecase-templates/${id}/`, json(champs, 'PATCH')),
  referencesCompetence: (id: number) =>
    requete<ReferenceCompetence[]>(`/documents/usecase-templates/${id}/documents/`),
  async importerCompetence(fichier: File) {
    const corps = new FormData();
    corps.append('fichier', fichier, fichier.name);
    const reponse = await appelAuthentifie(buildApiUrl('/documents/memoire/competences/importer/'), {
      method: 'POST',
      body: corps,
    });
    const donnees = await reponse.json().catch(() => ({}));
    if (!reponse.ok) throw new Error(donnees.detail || "La compétence n'a pas pu être importée.");
    return donnees as Competence;
  },

  modeles: () => requete<ReglagesModeles>('/documents/memoire/modeles/'),
  reglerModele: (champs: { usage?: string; assistant?: number; modele: string }) =>
    requete<ReglagesModeles>('/documents/memoire/modeles/', json(champs)),

  conflits: (statut: 'ouvert' | 'tranche' = 'ouvert') =>
    requete<ConflitMemoire[]>(`/documents/memoire/conflits/?statut=${statut}`),
  trancher: (id: number, gagnant: number) =>
    requete<{ tranche: boolean }>(`/documents/memoire/conflits/${id}/trancher/`, json({ gagnant })),
};
